import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { movies } from "@/mocks/movies";
import MovieGrid from "@/components/feature/MovieGrid";
import SectionHeader from "@/components/feature/SectionHeader";
import HeroCarousel from "./components/HeroCarousel";
import CategoryShortcuts from "./components/CategoryShortcuts";
import { useSeo } from "@/hooks/useSeo";

export default function Home() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");

  useSeo({
    title: "饥渴的岳中文字幕－提供高清中文字幕影视资源",
    description:
      "饥渴的岳中文字幕网站专注于提供饥渴的岳电影的中文字幕版本，涵盖最新影视资源，方便用户在线观看和查找相关剧情介绍。",
    keywords: "饥渴的岳,中文字幕,饥渴的岳电影,中文字幕下载,中文字幕影视,电影资源,高清影视",
  });

  const latest = [...movies].sort((a, b) => b.year - a.year || b.rating - a.rating).slice(0, 10);
  const popular = [...movies].sort((a, b) => b.views - a.views).slice(0, 5);
  const topRated = [...movies].sort((a, b) => b.rating - a.rating).slice(0, 10);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <div>
      <HeroCarousel />

      <div className="w-full px-4 md:px-8 -mt-10 md:-mt-14 relative z-10">
        <form
          onSubmit={submit}
          className="flex items-center gap-2 bg-background-900 border border-background-700 rounded-lg md:rounded-full pl-4 pr-2 h-14 md:h-16 w-full max-w-3xl mx-auto focus-within:border-accent-600 transition-colors"
        >
          <i className="ri-search-line text-foreground-400 text-xl" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="输入影片名称、导演或类型，例如：暗夜之城、陈屿舟、悬疑"
            className="flex-1 bg-transparent border-none outline-none text-sm md:text-base text-foreground-100 placeholder:text-foreground-500 font-label min-w-0"
          />
          <button
            type="submit"
            className="h-10 md:h-12 px-4 md:px-6 rounded-md md:rounded-full bg-primary-600 hover:bg-primary-500 text-background-50 flex items-center gap-2 transition-colors cursor-pointer shrink-0 whitespace-nowrap"
          >
            <i className="ri-search-line" />
            <span className="hidden sm:inline text-sm font-label">搜索</span>
          </button>
        </form>
      </div>

      <section className="w-full px-4 md:px-8 pt-12 md:pt-16">
        <SectionHeader
          title="影视资源快捷分类"
          subtitle="按字幕版本快速定位你需要的《饥渴的岳》资源"
          moreTo="/category"
        />
        <CategoryShortcuts />
      </section>

      <section className="w-full px-4 md:px-8 pt-14 md:pt-20">
        <SectionHeader
          title="最新影片"
          subtitle="按上映年份与口碑排序的近期中文字幕资源"
          moreTo="/category"
        />
        <MovieGrid movies={latest} />
      </section>

      <section className="w-full px-4 md:px-8 pt-14 md:pt-20">
        <SectionHeader
          title="热门影片精选"
          subtitle="近期浏览热度最高的《饥渴的岳》系列作品"
          moreTo="/category?sort=views"
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-5">
          {popular.map((movie, i) => (
            <Link
              key={movie.id}
              to={`/detail/${movie.id}`}
              className="group flex gap-4 bg-background-900 border border-background-800 rounded-lg p-3 md:p-4 hover:border-primary-700 transition-colors cursor-pointer"
            >
              <div className="relative w-24 sm:w-28 md:w-32 aspect-[2/3] shrink-0 rounded-md overflow-hidden bg-background-800">
                <img
                  src={movie.cover}
                  alt={`${movie.title}（${movie.year}）中文字幕封面`}
                  title={`${movie.title} 中文字幕`}
                  className="w-full h-full object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="flex-1 min-w-0 flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-heading text-foreground-50 font-semibold text-base md:text-lg line-clamp-2 group-hover:text-accent-400 transition-colors">
                    {movie.title}
                  </h3>
                  <span className="font-heading text-2xl md:text-3xl font-bold text-primary-600/70 leading-none shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-400 font-label mt-2">
                  <span className="text-accent-500 font-semibold">{movie.rating.toFixed(1)}分</span>
                  <span>{movie.year}</span>
                  <span>{movie.region}</span>
                  <span className="px-2 py-0.5 rounded bg-secondary-900/60 text-secondary-100">
                    {movie.version}
                  </span>
                </div>
                <p className="text-xs md:text-sm text-foreground-400 font-label leading-relaxed mt-2 line-clamp-2 hidden sm:block">
                  {movie.description}
                </p>
                <div className="mt-auto pt-2 flex items-center gap-1.5 text-xs text-foreground-500 font-label">
                  <i className="ri-eye-line" />
                  {movie.views.toLocaleString()} 次浏览
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pt-14 md:pt-20">
        <SectionHeader
          title="高分口碑榜"
          subtitle="评分最高的中文字幕资源，适合系列入门"
          moreTo="/category?sort=rating"
        />
        <MovieGrid movies={topRated} />
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="rounded-lg bg-primary-950 border border-primary-900/60 p-6 md:p-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-foreground-50 text-xl md:text-2xl font-bold mb-4">
              关于「饥渴的岳中文字幕」
            </h2>
            <p className="text-sm md:text-base text-foreground-300 leading-relaxed font-label mb-4">
              本站是面向华语观众的影视内容展示站，围绕《饥渴的岳》系列电影整理中文字幕资源信息，提供影片剧情、版本索引、评分口碑与快捷筛选，帮助你更快找到想看的中文字幕版本。
            </p>
            <p className="text-sm text-foreground-400 leading-relaxed font-label">
              温馨提示：站内播放区域仅作界面演示，不含真实播放接口；影片资料为演示内容，用于展示浏览与筛选体验。
            </p>
          </div>
          <div className="flex flex-col justify-center gap-4">
            {[
              { icon: "ri-movie-line", label: "影片资源", value: `${movies.length}` },
              { icon: "ri-translate-2", label: "中文字幕版本", value: "4 类" },
              { icon: "ri-calendar-line", label: "年份覆盖", value: "2020–2026" },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-md bg-background-900 text-accent-400 flex items-center justify-center text-xl shrink-0">
                  <i className={stat.icon} />
                </span>
                <div>
                  <p className="font-heading text-foreground-50 text-lg font-bold">{stat.value}</p>
                  <p className="text-xs text-foreground-400 font-label">{stat.label}</p>
                </div>
              </div>
            ))}
            <Link
              to="/about"
              className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-accent-500 hover:bg-accent-400 text-background-950 font-label text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              了解本站更多
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}